import React from "react";
import MuiGrid from "@material-ui/core/Grid";
import PropTypes from "prop-types";

/**
 * Grid component wrapper for responsive two-dimensional layouts.
 * Wraps Material-UI Grid with 12-column system.
 * 
 * @component
 * @example
 * <Grid container spacing={2}>
 *   <Grid item xs={12} md={6}>
 *     Left column
 *   </Grid>
 *   <Grid item xs={12} md={6}>
 *     Right column
 *   </Grid>
 * </Grid>
 */
const Grid = React.forwardRef(({ children, ...props }, ref) => {
  return <MuiGrid ref={ref} {...props}>{children}</MuiGrid>;
});

Grid.displayName = 'Grid';

Grid.propTypes = {
  /**
   * The content of the component
   */
  children: PropTypes.node,
  /**
   * If true, the component will have the flex container behavior
   */
  container: PropTypes.bool,
  /**
   * If true, the component will have the flex item behavior
   */
  item: PropTypes.bool,
  /**
   * Defines the space between the type item components
   */
  spacing: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      xs: PropTypes.number,
      sm: PropTypes.number,
      md: PropTypes.number,
      lg: PropTypes.number,
      xl: PropTypes.number,
    }),
  ]),
  /**
   * Defines the number of grids the component is going to use (xs breakpoint)
   */
  xs: PropTypes.oneOfType([
    PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, "auto", true]),
    PropTypes.bool,
  ]),
  /**
   * Defines the number of grids the component is going to use (sm breakpoint)
   */
  sm: PropTypes.oneOfType([
    PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, "auto", true]),
    PropTypes.bool,
  ]),
  /**
   * Defines the number of grids the component is going to use (md breakpoint)
   */
  md: PropTypes.oneOfType([
    PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, "auto", true]),
    PropTypes.bool,
  ]),
  /**
   * Defines the number of grids the component is going to use (lg breakpoint)
   */
  lg: PropTypes.oneOfType([
    PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, "auto", true]),
    PropTypes.bool,
  ]),
  /**
   * Defines the number of grids the component is going to use (xl breakpoint)
   */
  xl: PropTypes.oneOfType([
    PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, "auto", true]),
    PropTypes.bool,
  ]),
  /**
   * Defines the flex-direction style property
   */
  direction: PropTypes.oneOf(["row", "row-reverse", "column", "column-reverse"]),
  /**
   * Defines the justify-content style property
   */
  justifyContent: PropTypes.oneOf([
    "flex-start",
    "center",
    "flex-end",
    "space-between",
    "space-around",
    "space-evenly",
  ]),
  /**
   * Defines the align-items style property
   */
  alignItems: PropTypes.oneOf([
    "flex-start",
    "center",
    "flex-end",
    "stretch",
    "baseline",
  ]),
  /**
   * The component used for the root node
   */
  component: PropTypes.elementType,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])
    ),
    PropTypes.func,
    PropTypes.object,
  ]),
};

export default Grid;

